/* eslint-disable max-lines, max-len */
import { useMemo, useState } from 'react';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Badge,
  Box,
  Button,
  Divider,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useManageShipmentItemsContext } from './ManageShipmentItemsContext';

export const emptyFilter = {
  recipientName: '',
  fulfillingAt: '',
  isManual: '',
  matchPartner: false,
  matchPractice: false,
  city: '',
  state: '',
  zip: '',
};

export const defaultFilter = {
  ...emptyFilter,
  matchPartner: true,
  matchPractice: true,
};

const ManageShipmentItemsFilterButton = () => {
  const { filters, fulfillingPharmacies, shipment, applyFilters } = useManageShipmentItemsContext();

  const sortedPharmacies = useMemo(
    () =>
      [...(fulfillingPharmacies || [])].sort((left, right) =>
        String(left?.name ?? '').localeCompare(String(right?.name ?? ''), undefined, {
          sensitivity: 'base',
        })
      ),
    [fulfillingPharmacies]
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingFilters, setPendingFilters] = useState(emptyFilter);

  const activeFilterCount = [
    filters.recipientName,
    filters.fulfillingAt,
    filters.isManual,
    filters.matchPartner,
    filters.matchPractice,
    filters.city,
    filters.state,
    filters.zip,
  ].filter(Boolean).length;

  const hasPendingFilters = [
    pendingFilters.recipientName,
    pendingFilters.fulfillingAt,
    pendingFilters.isManual,
    pendingFilters.matchPartner,
    pendingFilters.matchPractice,
    pendingFilters.city,
    pendingFilters.state,
    pendingFilters.zip,
  ].some(Boolean);

  const canApplyFilters = hasPendingFilters || activeFilterCount > 0;

  const handleOpen = (e) => {
    setPendingFilters(filters);
    setAnchorEl(e.currentTarget);
  };

  const handleCancel = () => {
    setPendingFilters(filters);
    setAnchorEl(null);
  };

  const handleApply = () => {
    applyFilters(pendingFilters);
    setAnchorEl(null);
  };

  return (
    <>
      <Badge badgeContent={activeFilterCount} color='primary' sx={{ flexShrink: 0 }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={<TuneIcon fontSize='small' />}
          onClick={handleOpen}
          sx={{
            height: 32,
            flexShrink: 0,
            textTransform: 'none',
            fontSize: 12,
            borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
            color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary',
            bgcolor: 'background.paper',
            whiteSpace: 'nowrap',
          }}
        >
          Filter
        </Button>
      </Badge>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: 280,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant='body2' fontWeight={500} color='text.primary' sx={{ fontSize: 13 }}>
            Filter Shipments
          </Typography>
          <Button
            size='small'
            disabled={!hasPendingFilters && activeFilterCount === 0}
            onClick={() => setPendingFilters(emptyFilter)}
            sx={{
              textTransform: 'none',
              fontSize: 11.5,
              p: 0,
              minWidth: 0,
              color: 'primary.main',
              '&.Mui-disabled': { color: 'text.disabled' },
            }}
          >
            Reset
          </Button>
        </Box>

        <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: 11 }}
            >
              Recipient
            </Typography>
            <TextField
              fullWidth
              size='small'
              placeholder='e.g., Williams, Olivia'
              value={pendingFilters.recipientName}
              onChange={(e) =>
                setPendingFilters((prev) => ({ ...prev, recipientName: e.target.value }))
              }
              InputProps={{ sx: { fontSize: 12 } }}
            />
          </Box>

          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: 11 }}
            >
              Fulfilling At
            </Typography>
            <Select
              fullWidth
              size='small'
              displayEmpty
              value={pendingFilters.fulfillingAt}
              onChange={(e) =>
                setPendingFilters((prev) => ({ ...prev, fulfillingAt: e.target.value }))
              }
              sx={{ fontSize: 12 }}
              renderValue={(val) => {
                const match = fulfillingPharmacies.find((p) => p.fulfillingPharmacyId === val);
                return (
                  <Typography
                    variant='body2'
                    sx={{ fontSize: 12, color: val ? 'text.primary' : 'text.disabled' }}
                  >
                    {match ? match.name : 'All pharmacies'}
                  </Typography>
                );
              }}
            >
              <MenuItem value='' sx={{ fontSize: 12 }}>
                All pharmacies
              </MenuItem>
              {sortedPharmacies.map((p) => (
                <MenuItem
                  key={p.fulfillingPharmacyId}
                  value={p.fulfillingPharmacyId}
                  sx={{ fontSize: 12 }}
                >
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontWeight: 500, fontSize: 11 }}
                >
                  Address
                </Typography>
                {(pendingFilters.city || pendingFilters.state || pendingFilters.zip) && (
                  <Button
                    size='small'
                    sx={{
                      textTransform: 'none',
                      fontSize: 10,
                      p: 0,
                      minWidth: 0,
                      color: 'text.disabled',
                      lineHeight: 1,
                      '&:hover': { color: 'error.main', bgcolor: 'transparent' },
                    }}
                    onClick={() =>
                      setPendingFilters((prev) => ({ ...prev, city: '', state: '', zip: '' }))
                    }
                  >
                    × Clear
                  </Button>
                )}
              </Box>
              <Button
                size='small'
                sx={{
                  textTransform: 'none',
                  fontSize: 11,
                  p: 0,
                  minWidth: 0,
                  color: 'primary.main',
                  lineHeight: 1,
                }}
                onClick={() => {
                  const addr = shipment.shipmentAddress;
                  setPendingFilters((prev) => ({
                    ...prev,
                    city: addr?.city || prev.city,
                    state: addr?.state || prev.state,
                    zip: addr?.zip || prev.zip,
                  }));
                }}
              >
                Use current
              </Button>
            </Box>
            {!(pendingFilters.city || pendingFilters.state || pendingFilters.zip) &&
              shipment.shipmentAddress?.city && (
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    fontSize: 10.5,
                    color: 'text.disabled',
                    mb: 0.75,
                    fontStyle: 'italic',
                  }}
                >
                  Current:{' '}
                  {[
                    shipment.shipmentAddress.city,
                    shipment.shipmentAddress.state,
                    shipment.shipmentAddress.zip,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              )}
            <TextField
              fullWidth
              size='small'
              placeholder='City'
              value={pendingFilters.city}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, city: e.target.value }))}
              InputProps={{ sx: { fontSize: 12 } }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: '0 0 80px' }}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: 11 }}
              >
                State
              </Typography>
              <TextField
                fullWidth
                size='small'
                placeholder='CA'
                value={pendingFilters.state}
                onChange={(e) => setPendingFilters((prev) => ({ ...prev, state: e.target.value }))}
                InputProps={{ sx: { fontSize: 12 }, inputProps: { maxLength: 2 } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: 11 }}
              >
                ZIP
              </Typography>
              <TextField
                fullWidth
                size='small'
                placeholder='90210'
                value={pendingFilters.zip}
                onChange={(e) => setPendingFilters((prev) => ({ ...prev, zip: e.target.value }))}
                InputProps={{ sx: { fontSize: 12 }, inputProps: { maxLength: 10 } }}
              />
            </Box>
          </Box>

          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: 11 }}
            >
              Shipment Type
            </Typography>
            <Select
              fullWidth
              size='small'
              displayEmpty
              value={pendingFilters.isManual}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, isManual: e.target.value }))}
              sx={{ fontSize: 12 }}
              renderValue={(val) => {
                const labels = { manual: 'Manual only', 'non-manual': 'Non-manual only' };
                return (
                  <Typography
                    variant='body2'
                    sx={{ fontSize: 12, color: val ? 'text.primary' : 'text.disabled' }}
                  >
                    {labels[val] || 'All shipments'}
                  </Typography>
                );
              }}
            >
              <MenuItem value='' sx={{ fontSize: 12 }}>
                All shipments
              </MenuItem>
              <MenuItem value='manual' sx={{ fontSize: 12 }}>
                Manual only
              </MenuItem>
              <MenuItem value='non-manual' sx={{ fontSize: 12 }}>
                Non-manual only
              </MenuItem>
            </Select>
          </Box>

          <Divider />

          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 0.75, fontWeight: 500, fontSize: 11 }}
            >
              Match Current Shipment
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Box>
                <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 500 }}>
                  Partner
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                  {shipment.partnerName || '—'}
                </Typography>
              </Box>
              <Switch
                size='small'
                checked={pendingFilters.matchPartner}
                onChange={(e) =>
                  setPendingFilters((prev) => ({ ...prev, matchPartner: e.target.checked }))
                }
                color='primary'
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 500 }}>
                  Practice
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                  {shipment.practiceName || '—'}
                </Typography>
              </Box>
              <Switch
                size='small'
                checked={pendingFilters.matchPractice}
                onChange={(e) =>
                  setPendingFilters((prev) => ({ ...prev, matchPractice: e.target.checked }))
                }
                color='primary'
              />
            </Box>
          </Box>
        </Box>

        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Button
            size='small'
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            size='small'
            sx={{
              textTransform: 'none',
              fontSize: 12,
              px: 2,
              fontWeight: 500,
              ...(!canApplyFilters && {
                bgcolor: 'grey.200',
                color: 'text.secondary',
                boxShadow: 'none',
                '&:hover': { bgcolor: 'grey.300', boxShadow: 'none' },
              }),
            }}
            onClick={handleApply}
          >
            Apply Filters
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default ManageShipmentItemsFilterButton;
